import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { EvidenceNumber } from '../../../types/evidence-numbers'
import {
  useEvidenceStatusHistory,
  useEvidenceAmendments,
} from '../../../hooks/use-evidence-numbers'
import { EvidenceStatusBadge } from '../../../components/evidence/EvidenceStatusBadge'
import { EvidenceStatusTimeline } from './EvidenceStatusTimeline'

interface EvidenceDetailPanelProps {
  caseId: string
  evidenceNumber: EvidenceNumber | null
  onClose: () => void
}

interface DetailHeaderProps {
  evidenceNumber: EvidenceNumber
  onClose: () => void
}

function DetailHeader({ evidenceNumber, onClose }: DetailHeaderProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
      <div>
        <h2 className="font-mono text-lg font-semibold text-[var(--text-primary)]">
          {evidenceNumber.evidenceNumber}
        </h2>
        <div className="mt-1 flex items-center gap-2">
          <EvidenceStatusBadge status={evidenceNumber.currentStatus} />
          <span className="text-xs text-[var(--text-muted)]">
            {t(`type.${evidenceNumber.numberType}`)}
          </span>
        </div>
      </div>
      <button onClick={onClose} className="rounded p-1 hover:bg-[var(--bg-hover)]">
        <X className="h-5 w-5 text-[var(--text-muted)]" />
      </button>
    </div>
  )
}

function DetailInfo({ evidenceNumber }: { evidenceNumber: EvidenceNumber }): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="space-y-3 text-sm">
      <InfoRow
        label={t('table.infrastructure')}
        value={evidenceNumber.endpointHostname ?? evidenceNumber.endpointName ?? '--'}
      />
      <InfoRow
        label={t('table.registered')}
        value={new Date(evidenceNumber.registeredAt).toLocaleString()}
      />
      {evidenceNumber.parentEvidenceNumber ? (
        <InfoRow label={t('table.parent')} value={evidenceNumber.parentEvidenceNumber} mono />
      ) : null}
      {evidenceNumber.collectionRequestName ? (
        <InfoRow
          label={t('table.collectionRequest')}
          value={evidenceNumber.collectionRequestName}
        />
      ) : null}
      {evidenceNumber.notes ? <InfoRow label="Notes" value={evidenceNumber.notes} /> : null}
    </div>
  )
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}): React.ReactElement {
  return (
    <div className="flex justify-between">
      <span className="text-[var(--text-muted)]">{label}</span>
      <span className={`text-[var(--text-primary)] ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  )
}

function PanelContent({
  caseId,
  evidenceNumber,
}: {
  caseId: string
  evidenceNumber: EvidenceNumber
}): React.ReactElement {
  const { t } = useTranslation('evidence')
  const { entries: history, loading: historyLoading } = useEvidenceStatusHistory(
    caseId,
    evidenceNumber.id
  )
  const isBase = evidenceNumber.numberType === 'base'
  const { amendments, loading: amendmentsLoading } = useEvidenceAmendments(
    isBase ? caseId : null,
    isBase ? evidenceNumber.id : null
  )

  return (
    <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
      <DetailInfo evidenceNumber={evidenceNumber} />

      <Section title={t('detail.statusTimeline')}>
        <EvidenceStatusTimeline entries={history} loading={historyLoading} />
      </Section>

      {isBase ? (
        <Section title={t('detail.amendments')}>
          <AmendmentsList amendments={amendments} loading={amendmentsLoading} />
        </Section>
      ) : null}
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold tracking-wider text-[var(--text-muted)] uppercase">
        {title}
      </h3>
      {children}
    </div>
  )
}

function AmendmentsList({
  amendments,
  loading,
}: {
  amendments: EvidenceNumber[]
  loading: boolean
}): React.ReactElement {
  const { t } = useTranslation('evidence')

  if (loading) return <div className="h-16 animate-pulse rounded bg-[var(--bg-card)]" />
  if (amendments.length === 0)
    return <p className="text-sm text-[var(--text-muted)] italic">{t('detail.noAmendments')}</p>

  return (
    <div className="space-y-2">
      {amendments.map((amendment) => (
        <div
          key={amendment.id}
          className="flex items-center justify-between rounded border border-[var(--border)] px-3 py-2"
        >
          <span className="font-mono text-sm">{amendment.evidenceNumber}</span>
          <EvidenceStatusBadge status={amendment.currentStatus} />
        </div>
      ))}
    </div>
  )
}

export function EvidenceDetailPanel({
  caseId,
  evidenceNumber,
  onClose,
}: EvidenceDetailPanelProps): React.ReactElement | null {
  if (!evidenceNumber) return null

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-[var(--border)] bg-[var(--bg-card)] shadow-xl">
      <DetailHeader evidenceNumber={evidenceNumber} onClose={onClose} />
      <PanelContent caseId={caseId} evidenceNumber={evidenceNumber} />
    </div>
  )
}
